import React, { useState } from 'react';
import { useReviewsTools } from '@app/site/secured/review-manager/provider/useReviewsTools';
import MainPage from '@app/component/page/MainPage';
import ReviewsCompanyHotelButtonFilter
	from '@app/site/secured/review-manager/fragment/reviews-inbox/filter/ReviewsCompanyHotelButtonFilter';
import { useQuery } from '@tanstack/react-query';
import { ReviewService } from '@app/model/review/service/ReviewService';
import { ReviewsQueryKeys } from '@app/model/review/service/ReviewsQueryKeys';
import Message from '@app/component/message/Message';
import ReviewsPagePlaceholder from '@app/component/placeholder/ReviewsPagePlaceholder';
import _isEmpty from 'lodash/isEmpty';
import ReviewsPropertyPerformanceTable
	from '@app/site/secured/review-manager/fragment/property-overview/component/ReviewsPropertyPerformanceTable';
import ReviewsCompetitorsOverviewPage
	from '@app/site/secured/review-manager/fragment/property-overview/_competitors/ReviewsCompetitorsOverviewPage';
import ReviewsPerformanceTrendsGraph
	from '@app/site/secured/review-manager/fragment/property-overview/component/graph/ReviewsPerformanceTrendsGraph';
import ReviewsPlatformDistributionGraph
	from '@app/site/secured/review-manager/fragment/property-overview/component/graph/ReviewsPlatformDistributionGraph';
import PageTabs from '@app/component/tabs/PageTabs';
import ReviewsStateStatisticsFragment
	from '@app/site/secured/review-manager/fragment/property-overview/component/ReviewsStateStatisticsFragment';
import ReviewsStateForPeriod from '@app/model/review/state/ReviewsStateForPeriod';
import ReviewsPropertyInsights
	from '@app/site/secured/review-manager/fragment/property-overview/_insights/ReviewsPropertyInsights';
import MenuButtonFilter, { MenuButtonFilterItem } from '@app/component/menu/MenuButtonFilter';
import ReviewsStateFilter from '@app/model/review/input/ReviewsStateFilter';


const ReviewsPropertyOverviewPage = React.memo(() => {

	const tools = useReviewsTools();

	return <MainPage>
		<div className="mt-6">
			<div className="sm:flex items-center justify-between">
				<div className="flex items-center sm:space-x-5 mb-2 sm:mb-0">
					<ReviewsCompanyHotelButtonFilter filter={tools.filter} onFilter={tools.setFilter} isStandAlone />
				</div>
			</div>
		</div>
		<ReviewsPropertyOverviewLoader />
	</MainPage>;
});

const ReviewsPropertyOverviewLoader = React.memo(() => {
	const tools = useReviewsTools();
	const [period, setPeriod] = useState<number | undefined>(undefined);
	const reviewsStateFilter: ReviewsStateFilter | undefined = period !== undefined ? { months: period } : undefined;
	const comparisonReviewsStateFilter: ReviewsStateFilter | undefined = period !== undefined ? {
		months: period,
		offset: period,
	} : undefined;


	const { data, isLoading, isSuccess } = useQuery({
		queryFn: () => ReviewService.findReviewStatesForPeriod(tools.filter.companyHotelId, reviewsStateFilter),
		queryKey: ReviewsQueryKeys.reviewsStateForPeriod(tools.filter.companyHotelId, reviewsStateFilter),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
	});

	const { data: comparisonData, isLoading: isComparisonLoading } = useQuery({
		queryFn: () => ReviewService.findReviewStatesForPeriod(tools.filter.companyHotelId, comparisonReviewsStateFilter),
		queryKey: ReviewsQueryKeys.reviewsStateForPeriod(tools.filter.companyHotelId, comparisonReviewsStateFilter),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		enabled: period !== undefined,
	});

	if (isLoading || (period !== undefined && isComparisonLoading))
		return <ReviewsPagePlaceholder />;

	if (isSuccess) {

		if (_isEmpty(data))
			return <Message state="warning">No reviews statistics found for this hotel!</Message>;
		else
			return <ReviewsPropertyOverview
				reviewsStateForPeriod={data}
				comparisonReviewsStateForPeriod={period !== undefined ? comparisonData ?? undefined : undefined}
				period={period}
				onPeriodChange={setPeriod}
			/>;
	}
	return <Message state="error">
		Property Overview cannot be loaded at the moment. Please retry again later!
	</Message>;

});

const ReviewsPropertyOverview: React.FunctionComponent<{
	reviewsStateForPeriod: ReviewsStateForPeriod,
	comparisonReviewsStateForPeriod?: ReviewsStateForPeriod,
	period: number | undefined,
	onPeriodChange: (months: number | undefined) => void
}> = ({ reviewsStateForPeriod, comparisonReviewsStateForPeriod, period, onPeriodChange }) => {

	const mainHotelReviewsState = reviewsStateForPeriod.findMainHotelState();
	const comparisonMainHotelReviewsState = comparisonReviewsStateForPeriod?.findMainHotelState();

	if (mainHotelReviewsState !== undefined) {
		return <>

			<PageTabs options={[
				{
					label: 'Insights',
					link: '/secured/review-manager/property-overview/insights',
					isRouter: true,
					fragment: <>
						<ReviewsPropertyOverviewTabToolbar />
						<ReviewsPropertyInsights />
					</>,
				},
				{
					label: 'Overview',
					link: '/secured/review-manager/property-overview/overview',
					fragment: <>
						<ReviewsPropertyOverviewTabToolbar hasPeriodFilter period={period} onPeriodChange={onPeriodChange} />
						<ReviewsStateStatisticsFragment
							statistics={mainHotelReviewsState.statistics}
							rank={reviewsStateForPeriod.rank}
						/>
						<ReviewsPropertyPerformanceTable
							selectedHotelReviewsState={mainHotelReviewsState}
							comparisonHotelReviewsState={comparisonMainHotelReviewsState}
						/>
						<div className="grid grid-cols-1 gap-5 sm:grid-cols-2 mt-4">
							<div className="col-span-2 md:col-span-1">
								<div className="px-3 mb-1">
									<h2 className="text-base font-semibold text-gray-900">2 Lines Chart</h2>
								</div>
								<div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm h-96">
									<ReviewsPerformanceTrendsGraph reviewsState={mainHotelReviewsState} />
								</div>
							</div>
							<div className="col-span-2 md:col-span-1">
								<div className="px-3 mb-1">
									<h2 className="text-base font-semibold text-gray-900">
										Pie chart
									</h2>
								</div>
								<div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm h-96">
									<ReviewsPlatformDistributionGraph reviewsState={mainHotelReviewsState} />
								</div>
							</div>
						</div>
					</>,
				},
				{
					label: 'Competitors',
					link: '/secured/review-manager/property-overview/competitors',
					fragment: <>
						<ReviewsPropertyOverviewTabToolbar hasPeriodFilter period={period} onPeriodChange={onPeriodChange} />
						<ReviewsStateStatisticsFragment
							statistics={mainHotelReviewsState.statistics}
							rank={reviewsStateForPeriod.rank}
						/>
						<ReviewsCompetitorsOverviewPage
							reviewsStateForPeriod={reviewsStateForPeriod}
							mainHotelReviewsState={mainHotelReviewsState}
						/>
					</>,
				},
			]} />
		</>;

	}


	return <Message state="warning" extraClassName="mt-6">There are no reviews found for this property!</Message>;
};

const ReviewsPropertyOverviewTabToolbar: React.FunctionComponent<{
	hasPeriodFilter?: boolean,
	period?: number,
	onPeriodChange?: (months: number | undefined) => void
}> = ({ hasPeriodFilter = false, period, onPeriodChange }) => {
	return <div className="mb-5 flex flex-wrap items-center justify-end gap-2">
		{hasPeriodFilter &&
			<ReviewsPropertyOverviewPeriodFilter period={period} onPeriodChange={onPeriodChange ?? (() => undefined)} />}
	</div>;
};

const ReviewsPropertyOverviewPeriodFilter: React.FunctionComponent<{
	period: number | undefined,
	onPeriodChange: (period: number | undefined) => void
}> = ({ period, onPeriodChange }) => {
	const menuItems: MenuButtonFilterItem<number | undefined>[] = [
		{ name: 'All time', value: undefined },
		{ name: 'Current month', value: 0 },
		{ name: 'Previous month', value: 1 },
		{ name: 'Last 3 months', value: 3 },
		{ name: 'Last 6 months', value: 6 },
		{ name: 'Last 12 months', value: 12 },
	];

	return <MenuButtonFilter
		menuItems={menuItems}
		selectedValue={period}
		onSelect={onPeriodChange}
		menuDescription="Showing reviews for the selected period of time."
		menuWidth="w-56"
		buttonHeight="h-8"
		textSize="text-xs"
		isActive={() => false}
	/>;
};


export default ReviewsPropertyOverviewPage;
